// Sending a period's paperwork to the accountant.
//
// Pick a period, tick the documents, send one archive. Sales invoices are
// rendered fresh; purchase invoices go as the original file the supplier sent,
// because that scan is what the accountant actually needs.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { ErrorAlert, LoadingRow, TextAreaField, TextField } from '../components/ui';
import { formatDate, formatMoney } from '@shared/format';
import type { ExportCandidate } from '@shared/types';

/** Year and quarter shortcuts, newest first. */
function periodPresets(): { label: string; from: string; to: string; year: number; quarter?: number }[] {
  const now = new Date();
  const presets: { label: string; from: string; to: string; year: number; quarter?: number }[] = [];
  for (let offset = 0; offset < 3; offset++) {
    const year = now.getFullYear() - offset;
    presets.push({ label: '', from: `${year}-01-01`, to: `${year}-12-31`, year });
    for (let quarter = 4; quarter >= 1; quarter--) {
      const startMonth = (quarter - 1) * 3 + 1;
      const endMonth = quarter * 3;
      const lastDay = new Date(year, endMonth, 0).getDate();
      presets.push({
        label: '',
        from: `${year}-${String(startMonth).padStart(2, '0')}-01`,
        to: `${year}-${String(endMonth).padStart(2, '0')}-${lastDay}`,
        year,
        quarter,
      });
    }
  }
  return presets;
}

export function Accounting() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const thisYear = new Date().getFullYear();
  const [from, setFrom] = useState(`${thisYear}-01-01`);
  const [to, setTo] = useState(`${thisYear}-12-31`);
  const [onlyUnsent, setOnlyUnsent] = useState(false);
  const [selectedSales, setSelectedSales] = useState<Set<number>>(new Set());
  const [selectedPurchases, setSelectedPurchases] = useState<Set<number>>(new Set());

  const candidates = useAsync(
    () => api.exportCandidates(from, to, onlyUnsent), [from, to, onlyUnsent]);
  const defaults = useAsync(() => api.exportDefaults(from, to, locale), [from, to, locale]);

  // Everything in the period starts ticked: sending the lot is the common case,
  // and unticking the odd exception is less work than ticking forty invoices.
  useEffect(() => {
    if (!candidates.data) return;
    setSelectedSales(new Set(candidates.data.sales.map((c) => c.id)));
    setSelectedPurchases(new Set(candidates.data.purchases.map((c) => c.id)));
  }, [candidates.data]);

  const currency = (value: number) => formatMoney(value, money);
  const presets = useMemo(periodPresets, []);

  const selectedCount = selectedSales.size + selectedPurchases.size;
  const missingFiles = (candidates.data?.purchases ?? [])
    .filter((c) => selectedPurchases.has(c.id) && !c.hasFile).length;

  const selectedTotal = (list: ExportCandidate[], selected: Set<number>) =>
    list.filter((c) => selected.has(c.id)).reduce((sum, c) => sum + c.total, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{t('accounting.title')}</h1>
          <p className="page-head__subtitle">{t('accounting.subtitle')}</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__head"><h2 className="card__title">{t('accounting.period')}</h2></div>
        <div className="card__body stack">
          <div className="row">
            {presets.slice(0, 10).map((preset) => {
              const active = preset.from === from && preset.to === to;
              return (
                <button
                  key={`${preset.from}-${preset.to}`}
                  type="button"
                  className={`btn btn--sm${active ? ' btn--primary' : ''}`}
                  onClick={() => { setFrom(preset.from); setTo(preset.to); }}
                >
                  {preset.quarter
                    ? t('accounting.quickQuarter', { quarter: preset.quarter, year: preset.year })
                    : t('accounting.quickYear', { year: preset.year })}
                </button>
              );
            })}
          </div>
          <div className="form-grid form-grid--2">
            <TextField label={t('accounting.from')} type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} />
            <TextField label={t('accounting.to')} type="date" value={to}
              onChange={(e) => setTo(e.target.value)} />
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={onlyUnsent}
              onChange={(e) => setOnlyUnsent(e.target.checked)} />
            <span>{t('accounting.onlyUnsent')}</span>
          </label>
        </div>
      </div>

      {candidates.error ? <ErrorAlert message={candidates.error} onRetry={candidates.reload} />
        : candidates.loading && !candidates.data ? <LoadingRow />
        : (
          <div className="stack">
            <DocumentList
              title={t('accounting.sales')}
              items={candidates.data?.sales ?? []}
              selected={selectedSales}
              onChange={setSelectedSales}
              total={currency(selectedTotal(candidates.data?.sales ?? [], selectedSales))}
            />
            <DocumentList
              title={t('accounting.purchases')}
              items={candidates.data?.purchases ?? []}
              selected={selectedPurchases}
              onChange={setSelectedPurchases}
              total={currency(selectedTotal(candidates.data?.purchases ?? [], selectedPurchases))}
              showFileColumn
            />

            {missingFiles > 0 && (
              <div className="alert alert--warning">
                <Icon name="alert-circle" />
                <div>{t('accounting.noFileWarning', { count: missingFiles })}</div>
              </div>
            )}

            <SendForm
              from={from}
              to={to}
              defaults={defaults.data}
              loading={defaults.loading}
              selectedCount={selectedCount}
              salesIds={[...selectedSales]}
              purchaseIds={[...selectedPurchases]}
              onSent={(count, recipient) => {
                toast.success(t('accounting.sent', { count, to: recipient }));
                candidates.reload();
              }}
              onError={(err) => toast.error(toMessage(err))}
            />
          </div>
        )}
    </>
  );
}

function DocumentList({ title, items, selected, onChange, total, showFileColumn }: {
  title: string;
  items: ExportCandidate[];
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
  total: string;
  showFileColumn?: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">{title}</h2>
        <div className="row">
          <span className="muted">{t('accounting.selected', { count: selected.size })} · {total}</span>
          <button type="button" className="btn btn--sm" disabled={items.length === 0}
            onClick={() => onChange(new Set(items.map((i) => i.id)))}>
            {t('accounting.selectAll')}
          </button>
          <button type="button" className="btn btn--sm" disabled={selected.size === 0}
            onClick={() => onChange(new Set())}>
            {t('accounting.selectNone')}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          <div className="empty__hint">{t('accounting.noneInPeriod')}</div>
        </div>
      ) : (
        <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th className="shrink" />
                <th className="shrink">{t('invoices.number')}</th>
                <th className="shrink">{t('invoices.date')}</th>
                <th>{t('reports.client')}</th>
                {showFileColumn && <th className="shrink">{t('purchases.file')}</th>}
                <th className="num">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="shrink">
                    <input type="checkbox" checked={selected.has(item.id)}
                      onChange={() => toggle(item.id)}
                      aria-label={`${item.number} ${item.partyName}`} />
                  </td>
                  <td className="shrink mono">{item.number}</td>
                  <td className="shrink nowrap">{formatDate(item.issuedOn, locale)}</td>
                  <td>
                    <div className="truncate">{item.partyName}</div>
                    {item.lastExportedAt && (
                      <div className="table__meta">
                        {t('accounting.alreadySent', {
                          date: formatDate(item.lastExportedAt, locale) })}
                      </div>
                    )}
                  </td>
                  {showFileColumn && (
                    <td className="shrink">
                      {item.hasFile
                        ? <Icon name="check-circle" size={15} />
                        : <span className="badge badge--pending">{t('purchases.fileMissing')}</span>}
                    </td>
                  )}
                  <td className="num">{formatMoney(item.total, money)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SendForm({
  from, to, defaults, loading, selectedCount, salesIds, purchaseIds, onSent, onError,
}: {
  from: string;
  to: string;
  defaults: { to: string; subject: string; message: string; enabled: boolean } | undefined;
  loading: boolean;
  selectedCount: number;
  salesIds: number[];
  purchaseIds: number[];
  onSent: (count: number, recipient: string) => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [recipient, setRecipient] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  // The defaults change with the period, so the draft follows unless edited.
  useEffect(() => {
    if (!defaults) return;
    setRecipient(defaults.to);
    setSubject(defaults.subject);
    setMessage(defaults.message);
  }, [defaults]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSending(true);
    setErrors({});
    try {
      const result = await api.sendExport(from, to, locale, {
        salesInvoiceIds: salesIds,
        supplierInvoiceIds: purchaseIds,
        to: recipient.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        message,
      });
      onSent(result.count, result.to);
    } catch (err) {
      setErrors(fieldErrors(err));
      onError(err);
    } finally {
      setSending(false);
    }
  }

  if (loading && !defaults) return <LoadingRow />;

  return (
    <form onSubmit={onSubmit} noValidate className="card">
      <div className="card__head">
        <h2 className="card__title">{t('accounting.send')}</h2>
        <span className="muted">{t('accounting.archiveHint')}</span>
      </div>
      <div className="card__body stack">
        {defaults && !defaults.enabled && <ErrorAlert message={t('email.notConfigured')} />}
        {defaults?.enabled && !defaults.to && (
          <div className="alert alert--warning">
            <Icon name="alert-circle" />
            <div>{t('accounting.recipientMissing')}</div>
          </div>
        )}

        <div className="form-grid form-grid--2">
          <TextField label={t('email.to')} type="email" required value={recipient}
            error={errors.to} onChange={(e) => setRecipient(e.target.value)} />
          <TextField label={t('email.cc')} type="email" optional value={cc}
            error={errors.cc} onChange={(e) => setCc(e.target.value)} />
          <TextField label={t('email.subject')} required wrapperClassName="span-all"
            value={subject} error={errors.subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <TextAreaField label={t('email.message')} rows={9} value={message}
          onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className="card__foot">
        <span className="muted">{t('accounting.selected', { count: selectedCount })}</span>
        <button type="submit" className="btn btn--primary"
          disabled={sending || selectedCount === 0 || !defaults?.enabled}>
          {sending ? <span className="spinner" /> : <Icon name="send" />}
          {sending ? t('accounting.sending') : t('accounting.send')}
        </button>
      </div>
      {selectedCount === 0 && (
        <div className="card__body" style={{ paddingTop: 0 }}>
          <p className="field__hint">{t('accounting.nothingSelected')}</p>
        </div>
      )}
    </form>
  );
}
